import { Component } from '@theme/component';
import { onAnimationEnd, yieldToMainThread, prefersReducedMotion } from '@theme/utilities';
import { ThemeEvents, RecentlyViewedChangedEvent, RecentlyViewedRemovedEvent } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Shows/hides the visitor's recently viewed products. The carousel contains every product,
 * server-rendered (so styling, vendor and Bio/Vegan badges match collection pages exactly),
 * hidden by default; this only toggles visibility based on localStorage, which Liquid cannot
 * read, and orders the visible items by most-recently-viewed first.
 *
 * Removing a card fades it out first, then reflows the remaining cards into the gap (animated).
 * Undoing reverses that: the remaining cards shift back out of the way first, then the card fades
 * back in. The underlying recently-viewed state always commits immediately (not after the
 * animation), so a fast undo click can't race with the pending removal.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} container - Wraps the heading and carousel; hidden when there's nothing to show.
 * @property {HTMLElement} track - The carousel track holding every product's card (each hidden by default).
 *
 * @extends {Component<Refs>}
 */
class RecentlyViewedCarousel extends Component {
  requiredRefs = ['container', 'track'];

  /** @type {Set<HTMLElement>} Items currently mid hide/reveal-reflow, so a fast undo can't overlap with it. */
  #reflowing = new Set();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.recentlyViewedChanged, this.#onRecentlyViewedChanged);
    this.#reassignOrders();
    const viewedIds = RecentlyViewed.getProducts();
    for (const item of this.#allItems()) {
      item.hidden = this.#isExcluded(item) || !viewedIds.includes(item.dataset.productId ?? '');
    }
    this.#updateContainerVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.recentlyViewedChanged, this.#onRecentlyViewedChanged);
  }

  /**
   * Fades the card out, then (unless undone in the meantime) hides it and reflows the rest of the
   * carousel into the gap it leaves behind.
   * @param {{productId: string}} data - The product id to remove.
   * @param {Event} event - The click event.
   */
  removeItem = async (data, event) => {
    event.preventDefault();

    const item = this.#closestItem(event);

    // Already mid-fade from an earlier click on the same button; ignore the repeat.
    if (item?.classList.contains('is-removing')) return;

    document.dispatchEvent(
      new RecentlyViewedRemovedEvent({
        productId: data.productId,
        title: item?.dataset.productTitle ?? '',
        image: item?.dataset.productImage ?? '',
        index: RecentlyViewed.getProducts().indexOf(data.productId),
      })
    );

    item?.classList.add('is-removing');
    // Commits immediately so a fast undo click can't race with this method's own (delayed) state change.
    RecentlyViewed.removeProduct(data.productId);

    if (!item) return;

    // Yield first so the browser sees the class change before we look for animations to wait on.
    await yieldToMainThread();
    await onAnimationEnd(item);

    // Undone while fading: #onRecentlyViewedChanged already cancelled the fade and removed the class.
    if (!item.classList.contains('is-removing')) return;

    await this.#hideWithReflow(item);
  };

  /**
   * Re-renders when the recently-viewed list changes anywhere on the page (including this
   * carousel's own remove buttons, and undo via the toast).
   * @param {Event} event - The recently-viewed changed event.
   */
  #onRecentlyViewedChanged = (event) => {
    if (!(event instanceof RecentlyViewedChangedEvent)) return;

    const { productId, isPresent } = event.detail;
    const item = this.#findItem(productId);
    if (!item || this.#isExcluded(item)) return;

    if (isPresent) {
      if (this.#reflowing.has(item)) return; // its own hide/reveal sequence is already handling this
      if (item.classList.contains('is-removing') && !item.hidden) {
        // Undo while still fading out: nothing has shifted yet, just cancel the fade.
        item.classList.remove('is-removing');
      } else if (item.hidden) {
        // Undo after the removal had already fully completed: reflow the rest back, then fade in.
        this.#revealWithReflow(item);
      }

      return;
    }

    if (item.classList.contains('is-removing') || item.hidden || this.#reflowing.has(item)) return; // already being handled elsewhere

    // Removed via some other mechanism while this page happens to be open (e.g. another tab).
    item.hidden = true;
    item.style.order = '';
    this.#reassignOrders();
    this.#updateContainerVisibility();
  };

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #closestItem(event) {
    return event.target instanceof Element ? event.target.closest('.recently-viewed__item') : null;
  }

  /**
   * @param {string} productId
   * @returns {HTMLElement | null}
   */
  #findItem(productId) {
    return this.refs.track.querySelector(`.recently-viewed__item[data-product-id="${CSS.escape(productId)}"]`);
  }

  /** @returns {HTMLElement[]} */
  #allItems() {
    return Array.from(this.refs.track.querySelectorAll('.recently-viewed__item'));
  }

  /** @param {HTMLElement} item */
  #isExcluded(item) {
    const excludeId = this.dataset.excludeProductId;
    return !!excludeId && item.dataset.productId === excludeId;
  }

  /** @param {HTMLElement} exclude */
  #visibleSiblings(exclude) {
    return this.#allItems().filter((item) => item !== exclude && !item.hidden);
  }

  #reassignOrders() {
    const viewedIds = RecentlyViewed.getProducts();

    for (const item of this.#allItems()) {
      const index = viewedIds.indexOf(item.dataset.productId ?? '');
      if (index !== -1) item.style.order = String(index);
    }
  }

  #updateContainerVisibility() {
    this.refs.container.hidden = !this.#allItems().some((item) => !item.hidden);
  }

  /**
   * Hides the card and reflows its still-visible siblings into the gap it leaves, animated.
   * @param {HTMLElement} item
   */
  async #hideWithReflow(item) {
    this.#reflowing.add(item);

    const siblings = this.#visibleSiblings(item);
    const before = this.#captureRects(siblings);

    item.classList.remove('is-removing');
    item.hidden = true;
    item.style.order = '';
    this.#reassignOrders();
    this.#updateContainerVisibility();

    await this.#playFlip(siblings, before);

    this.#reflowing.delete(item);
  }

  /**
   * Reflows the still-visible siblings out of the way first, then reveals and fades the card in.
   * @param {HTMLElement} item
   */
  async #revealWithReflow(item) {
    this.#reflowing.add(item);

    const siblings = this.#visibleSiblings(item);
    const before = this.#captureRects(siblings);

    // Reserve its slot while staying invisible (opacity, not hidden) so the siblings' reflow
    // already accounts for it, without the card itself appearing yet.
    item.classList.add('is-removing');
    item.hidden = false;
    this.#reassignOrders();
    this.#updateContainerVisibility();

    await this.#playFlip(siblings, before);

    item.classList.remove('is-removing');
    this.#reflowing.delete(item);
  }

  /**
   * @param {HTMLElement[]} items
   * @returns {Map<HTMLElement, DOMRect>}
   */
  #captureRects(items) {
    return new Map(items.map((item) => [item, item.getBoundingClientRect()]));
  }

  /**
   * Classic FLIP: the DOM is already in its new state when this is called. Snaps each item back to
   * its pre-change position with no transition, then releases it next frame so it animates from
   * there to wherever it naturally landed.
   * @param {HTMLElement[]} items
   * @param {Map<HTMLElement, DOMRect>} before
   */
  async #playFlip(items, before) {
    if (prefersReducedMotion()) return;

    /** @type {HTMLElement[]} */
    const moved = [];

    for (const item of items) {
      const previous = before.get(item);
      if (!previous) continue;

      const next = item.getBoundingClientRect();
      const dx = previous.left - next.left;
      const dy = previous.top - next.top;

      if (dx === 0 && dy === 0) continue;

      item.style.transition = 'none';
      item.style.transform = `translate(${dx}px, ${dy}px)`;
      moved.push(item);
    }

    if (moved.length === 0) return;

    // Yield so the browser paints the snapped-back position before we release it into the transition.
    await yieldToMainThread();

    for (const item of moved) {
      item.style.transition = '';
      item.style.transform = '';
    }

    await onAnimationEnd(moved);
  }
}

if (!customElements.get('recently-viewed-carousel')) {
  customElements.define('recently-viewed-carousel', RecentlyViewedCarousel);
}
